import React, { useState, useRef, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Image,
  NativeSyntheticEvent,
  NativeScrollEvent,
} from 'react-native';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { SVG_COMPONENTS } from '@/assets/admin';
import { MARKETPLACE_COLORS } from '@/constants/residents/marketplaceColors';
import { RecentlyViewedItem } from '@/types/residents/marketplaceListing';

const PropertyPlaceholder = RESIDENTS_SVG_COMPONENTS.PROPERTY_PLACEHOLDER;
const ChevronLeftIcon = SVG_COMPONENTS.CHEVRON_LEFT;
const ChevronRightIcon = SVG_COMPONENTS.CHEVRON_RIGHT;

type LayoutMode = 'mobile' | 'tablet' | 'desktop';

interface RecentlyViewedSectionProps {
  items: RecentlyViewedItem[];
  onItemPress: (item: RecentlyViewedItem) => void;
  layoutMode?: LayoutMode;
}

// Base sizes for mobile
const MOBILE_ITEM_SIZE = 84;
const MOBILE_ITEM_GAP = 15;
const MOBILE_VISIBLE_ITEMS = 3.5;

// Larger sizes for tablet/desktop
const LARGE_ITEM_SIZE = 100;
const LARGE_ITEM_GAP = 20;
const LARGE_VISIBLE_ITEMS = 5.5;

export const RecentlyViewedSection: React.FC<RecentlyViewedSectionProps> = ({
  items,
  onItemPress,
  layoutMode = 'mobile',
}) => {
  const [activePageIndex, setActivePageIndex] = useState(0);
  const [scrollOffset, setScrollOffset] = useState(0);
  const [contentWidth, setContentWidth] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const scrollViewRef = useRef<ScrollView>(null);
  
  const isLargeScreen = layoutMode !== 'mobile';
  const isDesktop = layoutMode === 'desktop';
  
  // Responsive values
  const itemSize = isLargeScreen ? LARGE_ITEM_SIZE : MOBILE_ITEM_SIZE;
  const itemGap = isLargeScreen ? LARGE_ITEM_GAP : MOBILE_ITEM_GAP;
  const visibleItems = isLargeScreen ? LARGE_VISIBLE_ITEMS : MOBILE_VISIBLE_ITEMS;

  // Calculate total pages based on items
  const totalPages = Math.ceil(items.length / Math.floor(visibleItems));

  // Scroll step for arrow navigation (scroll by 3 items at a time)
  const scrollStep = (itemSize + itemGap) * 3;

  // Calculate max scroll offset from content and container dimensions
  const maxScrollOffset = Math.max(0, contentWidth - containerWidth);

  const handleScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    const contentOffsetX = event.nativeEvent.contentOffset.x;
    setScrollOffset(contentOffsetX);
    
    const pageWidth = (itemSize + itemGap) * Math.floor(visibleItems);
    const currentPage = Math.round(contentOffsetX / pageWidth);
    setActivePageIndex(Math.min(currentPage, totalPages - 1));
  };

  // Get content size when it changes (for calculating max scroll)
  const handleContentSizeChange = (width: number) => {
    setContentWidth(width);
  };

  // Get container layout dimensions
  const handleLayout = (event: { nativeEvent: { layout: { width: number } } }) => {
    setContainerWidth(event.nativeEvent.layout.width);
  };

  const handleScrollLeft = useCallback(() => {
    const newOffset = Math.max(0, scrollOffset - scrollStep);
    scrollViewRef.current?.scrollTo({ x: newOffset, animated: true });
  }, [scrollOffset, scrollStep]);

  const handleScrollRight = useCallback(() => {
    const newOffset = Math.min(maxScrollOffset, scrollOffset + scrollStep);
    scrollViewRef.current?.scrollTo({ x: newOffset, animated: true });
  }, [scrollOffset, scrollStep, maxScrollOffset]);

  const canScrollLeft = scrollOffset > 0;
  const canScrollRight = maxScrollOffset > 0 && scrollOffset < maxScrollOffset - 1;

  if (items.length === 0) {
    return null;
  }

  return (
    <View style={[
      styles.container,
      isLargeScreen && styles.containerLarge,
    ]}>
      {/* Title and Desktop Navigation Arrows */}
      <View style={[styles.headerRow, isLargeScreen && styles.headerRowLarge]}>
        <Text style={[
          styles.title,
          isLargeScreen && styles.titleLarge,
        ]}>Recently viewed</Text>
        
        {/* Desktop Arrow Navigation */}
        {isDesktop && items.length > Math.floor(visibleItems) && (
          <View style={styles.arrowsContainer}>
            <TouchableOpacity
              style={[
                styles.arrowButton,
                !canScrollLeft && styles.arrowButtonDisabled,
              ]}
              onPress={handleScrollLeft}
              disabled={!canScrollLeft}
              activeOpacity={0.7}
            >
              <ChevronLeftIcon 
                width={16} 
                height={16} 
                color={canScrollLeft ? MARKETPLACE_COLORS.textPrimary : MARKETPLACE_COLORS.textMuted} 
              />
            </TouchableOpacity>
            <TouchableOpacity
              style={[
                styles.arrowButton,
                !canScrollRight && styles.arrowButtonDisabled,
              ]}
              onPress={handleScrollRight}
              disabled={!canScrollRight}
              activeOpacity={0.7}
            >
              <ChevronRightIcon 
                width={16} 
                height={16} 
                color={canScrollRight ? MARKETPLACE_COLORS.textPrimary : MARKETPLACE_COLORS.textMuted} 
              />
            </TouchableOpacity>
          </View>
        )}
      </View>

      {/* Horizontal ScrollView of Items */}
      <ScrollView
        ref={scrollViewRef}
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={[
          styles.scrollContent,
          { gap: itemGap },
          isLargeScreen && styles.scrollContentLarge,
        ]}
        onScroll={handleScroll}
        onContentSizeChange={handleContentSizeChange}
        onLayout={handleLayout}
        scrollEventThrottle={16}
        decelerationRate="fast"
        snapToInterval={itemSize + itemGap}
      >
        {items.map((item) => (
          <TouchableOpacity
            key={item.id}
            style={[
              styles.itemContainer,
              { width: itemSize, height: itemSize },
              isLargeScreen && styles.itemContainerLarge,
            ]}
            onPress={() => onItemPress(item)}
            activeOpacity={0.8}
          >
            {item.image ? (
              <Image
                source={{ uri: item.image }}
                style={styles.itemImage}
                resizeMode="cover"
              />
            ) : (
              <View style={styles.placeholderContainer}>
                <PropertyPlaceholder width={itemSize * 0.8} height={itemSize * 0.8} />
              </View>
            )}
          </TouchableOpacity>
        ))}
      </ScrollView>

      {/* Carousel Dots - only show on mobile when there are multiple pages */}
      {totalPages > 1 && !isLargeScreen && (
        <View style={styles.dotsContainer}>
          <View style={styles.dotsWrapper}>
            {Array.from({ length: totalPages }).map((_, index) => (
              <View
                key={index}
                style={[
                  styles.dot,
                  index === activePageIndex && styles.dotActive,
                ]}
              />
            ))}
          </View>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: MARKETPLACE_COLORS.cardBackground,
    borderRadius: 16,
    paddingTop: 20,
    paddingBottom: 16,
    overflow: 'hidden',
  },
  containerLarge: {
    paddingTop: 24,
    paddingBottom: 24,
    borderRadius: 20,
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    marginBottom: 20,
  },
  headerRowLarge: {
    paddingHorizontal: 24,
    marginBottom: 24,
  },
  title: {
    fontSize: 16,
    fontWeight: '600',
    color: MARKETPLACE_COLORS.textPrimary,
    lineHeight: 24,
  },
  titleLarge: {
    fontSize: 18,
    lineHeight: 27,
  },
  arrowsContainer: {
    flexDirection: 'row',
    gap: 8,
  },
  arrowButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: MARKETPLACE_COLORS.white5Percent,
    alignItems: 'center',
    justifyContent: 'center',
  },
  arrowButtonDisabled: {
    opacity: 0.5,
  },
  scrollContent: {
    paddingHorizontal: 20,
    gap: MOBILE_ITEM_GAP,
  },
  scrollContentLarge: {
    paddingHorizontal: 24,
  },
  itemContainer: {
    width: MOBILE_ITEM_SIZE,
    height: MOBILE_ITEM_SIZE,
    borderRadius: 12,
    borderWidth: 0.45,
    borderColor: MARKETPLACE_COLORS.borderGray,
    overflow: 'hidden',
    backgroundColor: MARKETPLACE_COLORS.cardBackground,
  },
  itemContainerLarge: {
    borderRadius: 14,
  },
  itemImage: {
    width: '100%',
    height: '100%',
  },
  placeholderContainer: {
    width: '100%',
    height: '100%',
    backgroundColor: MARKETPLACE_COLORS.uploadAreaBackground,
    alignItems: 'center',
    justifyContent: 'center',
  },
  dotsContainer: {
    alignItems: 'center',
    marginTop: 16,
  },
  dotsWrapper: {
    flexDirection: 'row',
    backgroundColor: MARKETPLACE_COLORS.priceBadgeBg,
    borderRadius: 20,
    padding: 8,
    gap: 6,
  },
  dot: {
    width: 7,
    height: 7,
    borderRadius: 3.5,
    backgroundColor: MARKETPLACE_COLORS.dotInactive,
  },
  dotActive: {
    backgroundColor: MARKETPLACE_COLORS.dotActive,
  },
});

